// src/components/widgets/EmbedCodeModalShadcn.jsx
import React, { useEffect, useState } from 'react';
import {
  X,
  Copy,
  Check,
  Code,
  ExternalLink,
  BookOpen,
  Globe
} from 'lucide-react';

// shadcn components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';

import { useWidgets } from '../../hooks/useWidgets';
import { cn } from '../../utils/cn';

const EmbedCodeModalShadcn = ({ widgetId, onClose }) => {
  const { generateEmbedCode, getEmbedCode, generatingEmbed } = useWidgets();
  const [copiedSection, setCopiedSection] = useState(null);

  // Load embed code when modal opens
  useEffect(() => {
    if (widgetId) {
      generateEmbedCode(widgetId);
    }
  }, [widgetId, generateEmbedCode]);

  // Get the embed code data
  const embedData = getEmbedCode(widgetId);

  // Handle copy to clipboard
  const handleCopy = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (!embedData && !generatingEmbed) {
    return null;
  }

  // Generate different embed code variations
  const generateEmbedVariations = () => {
    if (!embedData) return {};

    const baseUrl = embedData.preview_url;

    return {
      basic: `<iframe src="${baseUrl}" width="100%" height="600px" style="border: none; border-radius: 8px;"></iframe>`,

      responsive: `<div style="position: relative; width: 100%; height: 0; padding-bottom: 75%;">
  <iframe
    src="${baseUrl}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
    allowfullscreen>
  </iframe>
</div>`,

      styled: `<div style="max-width: 600px; margin: 0 auto; padding: 20px;">
  <iframe
    src="${baseUrl}"
    width="100%"
    height="600px"
    style="border: none; border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
  </iframe>
</div>`
    };
  };

  const embedVariations = generateEmbedVariations();

  const platformInstructions = [
    {
      platform: 'WordPress',
      icon: Globe,
      steps: [
        'Edit the page where you want to add the booking widget',
        'Add a new "Custom HTML" or "HTML" block',
        'Paste the iframe code into the HTML block',
        'Save and publish your page'
      ]
    },
    {
      platform: 'Squarespace',
      icon: Globe,
      steps: [
        'Edit the page in Squarespace',
        'Add a new "Code" block',
        'Paste the iframe code',
        'Save and publish'
      ]
    },
    {
      platform: 'Wix',
      icon: Globe,
      steps: [
        'Go to your Wix editor',
        'Click "+ Add" and select "Embed Code"',
        'Choose "HTML iframe"',
        'Paste the code and adjust size',
        'Save and publish'
      ]
    },
    {
      platform: 'Webflow',
      icon: Globe,
      steps: [
        'Drag an "Embed" element to your page',
        'Paste the iframe code',
        'Set width to 100% and height to 600px',
        'Publish your site'
      ]
    },
    {
      platform: 'Custom HTML',
      icon: Code,
      steps: [
        'Open your HTML file',
        'Find where you want the widget',
        'Paste the iframe code',
        'Save and upload to your server'
      ]
    }
  ];

  const isOpen = !!(widgetId && (embedData || generatingEmbed));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Embed Code</DialogTitle>
          <DialogDescription>
            {embedData ? `For "${embedData.widget_name}"` : 'Loading...'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {generatingEmbed ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Generating embed code...</p>
              </div>
            </div>
          ) : embedData ? (
            <Tabs defaultValue="iframe" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="iframe" className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  iframe Embed
                </TabsTrigger>
                <TabsTrigger value="instructions" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Instructions
                </TabsTrigger>
              </TabsList>

              <TabsContent value="iframe" className="space-y-6">
                {/* Quick Copy */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Ready to Use</CardTitle>
                        <CardDescription>
                          The simplest way to embed your widget. Just copy and paste this code into any website.
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => handleCopy(embedData.embed_code, 'quick')}
                        className="flex items-center gap-2"
                      >
                        {copiedSection === 'quick' ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Code
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={embedData.embed_code}
                      readOnly
                      className="font-mono text-sm resize-none h-24"
                    />
                  </CardContent>
                </Card>

                {/* Advanced Options */}
                <Card>
                  <CardHeader>
                    <CardTitle>Advanced Options</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Responsive Embed */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Responsive Embed</h4>
                          <p className="text-sm text-muted-foreground">
                            Automatically adjusts to different screen sizes
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(embedVariations.responsive, 'responsive')}
                          className="flex items-center gap-1"
                        >
                          {copiedSection === 'responsive' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          Copy
                        </Button>
                      </div>
                      <Textarea
                        value={embedVariations.responsive}
                        readOnly
                        className="font-mono text-xs resize-none h-20"
                      />
                    </div>

                    {/* Styled Embed */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Styled with Container</h4>
                          <p className="text-sm text-muted-foreground">
                            Includes container styling with shadow and max-width
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(embedVariations.styled, 'styled')}
                          className="flex items-center gap-1"
                        >
                          {copiedSection === 'styled' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          Copy
                        </Button>
                      </div>
                      <Textarea
                        value={embedVariations.styled}
                        readOnly
                        className="font-mono text-xs resize-none h-20"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Preview Link */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Test Your Widget</h4>
                        <p className="text-sm text-muted-foreground">
                          Preview how your widget will look before embedding
                        </p>
                      </div>
                      <Button variant="outline" asChild>
                        <a
                          href={embedData.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open Preview
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="instructions" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>How to Add to Your Website</CardTitle>
                    <CardDescription>
                      Choose your platform below for step-by-step instructions
                    </CardDescription>
                  </CardHeader>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {platformInstructions.map((platform, index) => {
                    const Icon = platform.icon;

                    return (
                      <Card key={index}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Icon className="w-5 h-5" />
                            {platform.platform}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {platform.steps.map((step, stepIndex) => (
                              <li key={stepIndex} className="text-sm flex">
                                <span className="text-primary font-medium mr-2 flex-shrink-0">
                                  {stepIndex + 1}.
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* General Tips */}
                <Alert>
                  <AlertDescription>
                    <h4 className="font-medium mb-2">💡 Tips for Best Results</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Place the widget on a page where customers expect to book appointments</li>
                      <li>• Make sure the widget has enough space (at least 600px height)</li>
                      <li>• Test the widget on different devices to ensure it works properly</li>
                      <li>• Consider adding explanatory text above the widget</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {/* Support */}
                <Card>
                  <CardHeader>
                    <CardTitle>Need Help?</CardTitle>
                    <CardDescription>
                      Having trouble embedding your widget? We're here to help!
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Button variant="link" className="p-0 h-auto">
                        View Documentation
                      </Button>
                      <span className="text-muted-foreground">•</span>
                      <Button variant="link" className="p-0 h-auto">
                        Contact Support
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        {embedData && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-xs text-muted-foreground">
              Widget URL: <span className="font-mono">{embedData.preview_url}</span>
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmbedCodeModalShadcn;